import React from "react";
import {BaseGrid, FlexCol, FlexRow} from "./generic/Containers";
import {ARTICLES_MARKUP} from "../constants/ArticlesConfig";
import {CHALLENGES_TYPES_CONFIG} from "../constants/ChallengesTypesConfig";
import {REFERENCES_TYPES_CONFIG} from "../constants/ReferencesTypesConfig";
import {BCIcon, ICONS} from "./generic/BCIcons";
import {BCHeader3, BCHeader4, BCHeader5, Body1, Body2, Subtitle1} from "./generic/BCHeaders";
import {Article} from "../models/Article";
import "../css/GenericComponents.css";
import {trackEvent, TRACKING_EVENTS} from "../util/TrackingHelper";
import {BCPrimaryButton, BCMinorCTAButton, BUTTON_STYLES, ButtonStyle} from "./generic/BCButtons";

type ContentType = 'articles' | 'challenges' | 'references';

interface HotOffThePressAndChallengesSectionProps {
    navigate?: (path: string) => void;
    setSelectedContent?: (content: string, type: string) => void;
    isAuthenticated?: boolean;
    setShowAuthModal?: (show: boolean) => void;
    setAuthModalMode?: (mode: 'login' | 'register') => void;
}

export const HotOffThePressAndChallengesSection: React.FC<HotOffThePressAndChallengesSectionProps> = ({
    navigate,
    setSelectedContent,
    isAuthenticated,
    setShowAuthModal,
    setAuthModalMode
}) => {
    return (
        <FlexRow className={'highlight-challenges-container'} gap={1} widthPercent={100} reverseTablet={true} reverseMobile={true} style={{ alignItems: 'stretch' }}>
            <HighlightComponent 
                navigate={navigate} 
                setSelectedContent={setSelectedContent} 
                contentType={'articles'} 
                isAuthenticated={isAuthenticated}
                setShowAuthModal={setShowAuthModal}
                setAuthModalMode={setAuthModalMode}
            />
            <HighlightComponent 
                navigate={navigate} 
                setSelectedContent={setSelectedContent} 
                contentType={'challenges'}
                isAuthenticated={isAuthenticated}
                setShowAuthModal={setShowAuthModal}
                setAuthModalMode={setAuthModalMode}
            />
        </FlexRow>
    );
};

interface HighlightComponentProps {
    navigate?: (path: string) => void;
    setSelectedContent?: (content: string, type: string) => void;
    contentType: ContentType;
    isAuthenticated?: boolean;
    setShowAuthModal?: (show: boolean) => void;
    setAuthModalMode?: (mode: 'login' | 'register') => void;
}

// API Chip Component
const APIChip: React.FC<{api: any}> = ({api}) => (
    <FlexRow 
        gap={0.4} 
        align="center"
        padding={0.5}
        borderRadius={0.5}
        className="api-chip"
    >
        <img 
            src={api.mobileImage}
            alt={`${api.title} icon`}
            className="api-chip-icon"
        />
        <span className="api-chip-text">
            {api.title}
        </span>
    </FlexRow>
);

// API Chips Row Component
const APIChipsRow: React.FC<{apis: any[], startIndex: number, count: number}> = ({apis, startIndex, count}) => (
    <FlexRow gap={0.6}>
        {apis.slice(startIndex, startIndex + count).map((api: any, index: number) => (
            <APIChip key={startIndex + index} api={api} />
        ))}
    </FlexRow>
);

// API Chips Section Component
const APIChipsSection: React.FC<{referenceItems: any[]}> = ({referenceItems}) => (
    <FlexCol 
        flex={2} 
        gap={0.5}
        padding={0.4}
        className="api-chips-section"
    >
        <APIChipsRow apis={referenceItems} startIndex={0} count={5} />
        <APIChipsRow apis={referenceItems} startIndex={3} count={5} />
    </FlexCol>
);

export const HighlightComponent: React.FC<HighlightComponentProps> = ({navigate, setSelectedContent, contentType, isAuthenticated, setShowAuthModal, setAuthModalMode}) => {
    const isArticles = contentType === 'articles';
    const isChallenges = contentType === 'challenges';
    const isReferences = contentType === 'references';

    let contentData: any;
    let backgroundColor: string = '', borderColor: string = '', buttonStyle: ButtonStyle, shadowColor: string = '';

    if (isArticles) {
        contentData = {
            item: ARTICLES_MARKUP[0],
            title: "Hot Off the Press",
            icon: ICONS.PEN,
            dateField: 'date',
            navUrl: `/blog/${ARTICLES_MARKUP[0].navUrl}/`,
            contentTitle: ARTICLES_MARKUP[0].name,
            thumbnail: ARTICLES_MARKUP[0].thumbnail,
            itemName: ARTICLES_MARKUP[0].name,
            ctaText: 'Read Article'
        };
        backgroundColor = '#e8f5e8';
        borderColor = '1.5px solid #b8d9b8';
        buttonStyle = BUTTON_STYLES.GREEN;
        shadowColor = '0 6px 24px rgba(46, 125, 50, 0.10)';
    } else if (isChallenges) {
        contentData = {
            item: CHALLENGES_TYPES_CONFIG[0].challengeItems[0],
            title: "Challenge Yourself",
            icon: ICONS.SPARKLE,
            dateField: null,
            navUrl: `/challenges/${CHALLENGES_TYPES_CONFIG[0].navUrl}/`,
            contentTitle: CHALLENGES_TYPES_CONFIG[0].challengeItems[0].title,
            thumbnail: CHALLENGES_TYPES_CONFIG[0].thumbnail,
            itemName: CHALLENGES_TYPES_CONFIG[0].challengeItems[0].title,
            ctaText: 'Explore Challenges'
        };
        backgroundColor = '#ffe397';
        borderColor = '1.5px solid #fdcb6e';
        buttonStyle = BUTTON_STYLES.ORANGE;
        shadowColor = '0 6px 24px rgba(253, 203, 110, 0.15)';
    } else if (isReferences) {
        const beginnerAPIs = REFERENCES_TYPES_CONFIG.find(ref => ref.trackingId === 'beginnerFriendlyApis');
        
        contentData = {
            item: beginnerAPIs,
            title: "Handy References",
            icon: ICONS.SPARKLE,
            dateField: null,
            navUrl: '/references',
            contentTitle: 'Start Building with APIs',
            thumbnail: beginnerAPIs?.thumbnail || null,
            itemName: 'Start Building with APIs',
            ctaText: `Explore ${beginnerAPIs?.referenceItems.length || 0}+ APIs`
        };
        backgroundColor = '#faf5ff';
        borderColor = '1.5px solid #e9d5ff';
        buttonStyle = BUTTON_STYLES.PURPLE;
        shadowColor = '0 6px 24px rgba(139, 92, 246, 0.10)';
    } else {
        // Default case to ensure buttonStyle is always assigned
        buttonStyle = BUTTON_STYLES.GREEN;
    }

    const handleClick = () => {
        // Check if user is authenticated
        if (!isAuthenticated && setShowAuthModal && setAuthModalMode) {
            setAuthModalMode('register');
            setShowAuthModal(true);
            return;
        }

        if (isReferences) {
            trackEvent(TRACKING_EVENTS.HIGHLIGHT_SECTION_CLICKED, {
                content_type: 'start_building_with_apis',
                source: 'home_page'
            });
            trackEvent(TRACKING_EVENTS.PAGE_NAVIGATION, {
                content_id: 'references' 
            });
        } else {
            trackEvent(TRACKING_EVENTS.HIGHLIGHT_SECTION_CLICKED, {
                content_type: isArticles ? 'hot_off_the_press' : 'challenge_yourself',
                content_title: contentData.contentTitle,
                source: 'home_page'
            });
        }
        
        if (navigate) {
            navigate(contentData.navUrl);
        } else if (isReferences && setSelectedContent) {
            setSelectedContent('beginner-friendly-apis', 'references');
        }
    };

    return (
        <FlexCol
            className="highlight-component"
            gap={1.5}
            align="center"
            padding={1.2}
            widthPercent={100}
            borderRadius={0.8}
            onClick={handleClick}
            style={{
                background: backgroundColor,
                border: borderColor,
                boxShadow: shadowColor,
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                cursor: 'pointer',
            }}
        >
            <FlexCol gap={.8} widthPercent={100} style={{position: 'relative', zIndex: 1}}>
                <FlexRow gap={1} align="center">
                    <BCIcon icon={contentData.icon} style={{color: buttonStyle.background, fontSize: '1rem'}}/>
                    <BCHeader3 text={contentData.title} textAlign="center"
                               style={{color: buttonStyle.background, fontSize: '1rem'}}/>
                </FlexRow>

                <FlexRow gap={1} widthPercent={100} reverseMobile={true}>
                    {!isReferences && (
                        <img
                            className={'highlights-preview-image'}
                            src={contentData.thumbnail}
                            alt={contentData.title}
                            style={{
                                borderRadius: '0.5em',
                                objectFit: 'cover',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                flexShrink: 0,
                                width: '25%',
                                height: '140px'
                            }}
                        />
                    )}
                    <FlexCol className="highlight-content-area" gap={0.5} style={{flex: 1}}>
                        <BCHeader3 className={'highlights-preview-title'} text={contentData.itemName}
                                   style={{color: '#212529'}}/>
                        <Body1 text={contentData.item.description}
                               style={{color: '#6c757d', lineHeight: '1.5', marginBottom: '0.2em', fontSize: '.8rem'}}/>
                    </FlexCol>
                    {isReferences && (
                        <APIChipsSection referenceItems={contentData.item.referenceItems} />
                    )}
                </FlexRow>
            </FlexCol>

            {/*<BCMinorCTAButton*/}
            {/*    text={contentData.ctaText}*/}
            {/*    onClick={handleClick}*/}
            {/*    buttonStyle={buttonStyle}*/}
            {/*    position={"absolute"}*/}
            {/*    right={1}*/}
            {/*    bottom={1}*/}
            {/*    zIndex={2}*/}
            {/*/>*/}

            {contentData.item instanceof Article && (
                <div
                    style={{
                        position: 'absolute',
                        top: '1em',
                        right: '1em',
                        borderRadius: '0.5em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5em',
                        zIndex: 2}}
                >
                    <BCIcon icon={ICONS.CALENDAR} style={{color: '#6c757d', fontSize: '1em'}}/>
                    <Body2 text={contentData.item.date} style={{color: '#6c757d'}}/>
                </div>
            )}
        </FlexCol>
    );
};

interface FeaturedAPIsHighlightProps {
    setSelectedContent: (id: string, type: string) => void;
    navigate?: (path: string) => void;
    isAuthenticated?: boolean;
    setShowAuthModal?: (show: boolean) => void;
    setAuthModalMode?: (mode: 'login' | 'register') => void;
}

export const FeaturedAPIsHighlight: React.FC<FeaturedAPIsHighlightProps> = ({ 
    setSelectedContent, 
    navigate, 
    isAuthenticated, 
    setShowAuthModal, 
    setAuthModalMode 
}) => {
    const beginnerAPIs = REFERENCES_TYPES_CONFIG.find(ref => ref.trackingId === 'beginnerFriendlyApis');
    const featuredAPIs = beginnerAPIs?.referenceItems.slice(0, 6) || [];

    const handleClick = () => {
        // Check if user is authenticated
        if (!isAuthenticated && setShowAuthModal && setAuthModalMode) {
            setAuthModalMode('register');
            setShowAuthModal(true);
            return;
        } else {
            if (navigate) {
                navigate(`/references`);
            }
        }

        // Handle authenticated user click
        // window.open('https://discord.gg/WUdjg3mDfT', '_blank');
    };

    return (
        <div
            onClick={handleClick}
            style={{
                background: 'linear-gradient(135deg, #f8faff 0%, #e8f4f8 100%)',
                borderRadius: '1em',
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid #bfdbfe',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
            }}
            className="featured-apis-highlight"
            onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.15)';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            <div style={{
                position: 'absolute',
                top: '-30%',
                left: '-10%',
                width: '120%',
                height: '160%',
                background: 'radial-gradient(circle, #3b82f6 1px, transparent 1px)',
                backgroundSize: '25px 25px',
                opacity: 0.06,
                zIndex: 0
            }} />

            <div style={{
                position: 'absolute',
                top: '10%',
                right: '8%',
                width: '40px',
                height: '40px',
                background: '#3b82f6',
                borderRadius: '50%',
                animation: 'float 8s ease-in-out infinite',
                zIndex: 0,
                opacity: 0.1,
                filter: 'blur(1px)',
                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.2)'
            }} />
            <div style={{
                position: 'absolute',
                bottom: '15%',
                left: '5%',
                width: '30px',
                height: '30px',
                background: '#3b82f6',
                borderRadius: '50%',
                animation: 'float 6s ease-in-out infinite 2s',
                zIndex: 0,
                opacity: 0.08,
                filter: 'blur(1px)',
                boxShadow: '0 3px 15px rgba(59, 130, 246, 0.15)'
            }} />

            <FlexCol gap={1.5} widthPercent={100} align="center" style={{ position: 'relative', zIndex: 1, padding: '1.5em' }}>
                <FlexCol gap={.5} align="center">
                    <FlexRow gap={1} align="center">
                        <BCIcon icon={ICONS.SPARKLE} style={{
                            color: '#3b82f6',
                            fontSize: '1.8em',
                            filter: 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.2))'
                        }} />
                        <BCHeader3 text="Start Building with APIs" textAlign="center" style={{
                            color: 'var(--text-color)',
                            fontSize: '1.6em'
                        }} />
                    </FlexRow>
                    <Body1 text="Explore 30+ beginner-friendly APIs to add real-world data to your projects" textAlign="center" style={{
                        color: 'var(--secondary-text-color)',
                        maxWidth: '600px',
                        lineHeight: '1.5'
                    }} />
                </FlexCol>
                <BaseGrid gap={1} columns={3} mobileColumns={2} widthPercent={100}>
                    {featuredAPIs.map((api, index) => (
                        <FlexRow
                            key={index}
                            padding={1}
                            gap={0.8}
                            style={{
                                border: '1.5px solid #3b82f6',
                                borderRadius: '0.8em',
                                background: 'rgba(255, 255, 255, 0.7)',
                                backdropFilter: 'blur(10px)',
                                alignItems: 'center',
                                textAlign: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                            reverseMobile={true}
                            className="api-highlight-card"
                        >
                            <img src={api.mobileImage} alt={`${api.title} icon`}
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))'
                                }}
                            />
                            <FlexCol gap={0.3} widthPercent={100} justify={"flex-start"}>
                                <BCHeader4 className={'api-chip-title'} textAlign={"left"} text={api.title} style={{
                                    color: '#3b82f6',
                                    fontSize: '0.8em',
                                    fontWeight: '500',
                                    lineHeight: '1.2'
                                }} />
                                <Body2 className={'api-chip-text'} textAlign={"left"} text={api.description} style={{
                                    color: 'var(--secondary-text-color)',
                                    fontSize: '0.7em',
                                    lineHeight: '1',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }} />
                            </FlexCol>
                        </FlexRow>
                    ))}
                </BaseGrid>
            </FlexCol>
        </div>
    );
};

export const DiscordComponent: React.FC<{
    isAuthenticated?: boolean;
    setShowAuthModal?: (show: boolean) => void;
    setAuthModalMode?: (mode: 'login' | 'register') => void;
}> = ({ isAuthenticated, setShowAuthModal, setAuthModalMode }) => {
    const handleClick = () => {
        // Check if user is authenticated
        if (!isAuthenticated && setShowAuthModal && setAuthModalMode) {
            setAuthModalMode('register');
            setShowAuthModal(true);
            return;
        }

        // Handle authenticated user click - open Discord
        window.open('https://discord.gg/WUdjg3mDfT', '_blank');
    };

    return (
        <div
            style={{
                background: 'linear-gradient(135deg, #2c2f33 0%, #23272a 100%)',
                borderRadius: '0.8em',
                border: '1px solid #40444b',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
            }}
            className="discord-component"
            onClick={handleClick}
            onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
            }}
        >
            <FlexCol widthPercent={100} gap={1.5} align="center" pVertical={2} style={{ padding: '2em' }}>
                <FlexRow gap={1} align="center">
                    <BCIcon icon={ICONS.DISCORD} style={{
                        color: '#7289da',
                        fontSize: '2em',
                        filter: 'drop-shadow(0 2px 4px rgba(114, 137, 218, 0.3))'
                    }} />
                    <BCHeader3 text="Join Our Discord Community" textAlign="center" style={{ color: '#ffffff' }} />
                </FlexRow>
                <Subtitle1 text="Connect with other developers, get help with projects, and accelerate your career" textAlign="center" style={{ color: '#b9bbbe' }} />
                <BCMinorCTAButton
                    text="Join Discord Server"
                    onClick={handleClick}
                    buttonStyle={BUTTON_STYLES.PURPLE}
                />
            </FlexCol>
        </div>
    )
}